import { useState, useRef, useEffect } from 'react';
import { Add, Close, Search } from '@mui/icons-material';
import useAddRequestStore from 'store/addRequestStore';
import { useGetAmenities } from 'hooks/usePropertyRequests';

export default function AmenitiesSelector() {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [duplicateError, setDuplicateError] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { setRequestData, clearErrors, requestData } = useAddRequestStore();

    const { data: availableAmenities = [] } = useGetAmenities();

    const selectedAmenities: string[] = Array.isArray(requestData?.amenities)
        ? requestData.amenities
        : typeof requestData?.amenities === 'string'
          ? [requestData.amenities]
          : [];

    const suggestions = availableAmenities.filter((amenity: string) => !selectedAmenities.some((selected) => selected.toLowerCase() === amenity.toLowerCase()));

    const filteredAmenities = suggestions.filter((amenity: string) => amenity.toLowerCase().includes(searchQuery.toLowerCase()));

    const handleSelect = (amenity: string) => {
        if (selectedAmenities.some((selected) => selected.toLowerCase() === amenity.toLowerCase())) {
            setDuplicateError(`"${amenity}" has already been added.`);
            return;
        }
        const newSelectedAmenities = [...selectedAmenities, amenity];
        setRequestData({ amenities: newSelectedAmenities });
        clearErrors('amenities');
        setDuplicateError('');
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleRemove = (amenity: string) => {
        setRequestData({
            amenities: selectedAmenities.filter((a: string) => a.toLowerCase() !== amenity.toLowerCase()),
        });
        setDuplicateError('');
    };

    // Close dropdown when clicking outside.
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="w-full border bg-gray-50/10 rounded p-4">
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => {
                        setIsOpen(!isOpen);
                        setDuplicateError('');
                    }}
                    className="w-full px-4 lg:py-3 py-1.5 text-left text-gray-500 bg-white border rounded-lg focus:outline-none"
                >
                    Add Amenities
                </button>
                {isOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg">
                        <div className="p-2 border-b">
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search Amenities & View Preferences"
                                    className="w-full pl-10 pr-4 lg:py-3 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setDuplicateError('');
                                    }}
                                />
                            </div>
                            {duplicateError && <div className="mt-1 text-sm text-red-500">{duplicateError}</div>}
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                            {filteredAmenities.map((amenity: string) => (
                                <button
                                    key={amenity}
                                    onClick={() => handleSelect(amenity)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none"
                                >
                                    {amenity}
                                </button>
                            ))}
                            {filteredAmenities.length === 0 && searchQuery.trim() !== '' && (
                                <button
                                    onClick={() => handleSelect(searchQuery)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none font-medium text-blue-600"
                                >
                                    Add Amenity &quot;{searchQuery}&quot;
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {selectedAmenities.length > 0 && <Chip title="SELECTED" options={selectedAmenities} Icon={Close} handleRemove={handleRemove} />}

            {suggestions.length > 0 && <Chip title="SUGGESTIONS" options={suggestions} Icon={Add} handleRemove={handleSelect} isShowAll={false} />}
        </div>
    );
}

interface Props {
    title: string;
    options: string[];
    Icon: React.ElementType;
    handleRemove: (amenity: string) => void;
    isShowAll?: boolean;
}

function Chip({ title, options, Icon, handleRemove, isShowAll = true }: Props) {
    const showAll = isShowAll ? options : options.slice(0, 3);
    return (
        <div className="mt-4 w-full">
            <div className="text-sm font-medium text-gray-500 mb-2.5">{title}</div>
            <div className="flex flex-wrap gap-2">
                {showAll.map((amenity) => (
                    <span key={amenity} className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm text-light-black">
                        {amenity}
                        <button onClick={() => handleRemove(amenity)} className="ml-2 focus:outline-none">
                            <Icon className={`h-4 w-4 ${!isShowAll ? 'text-my-blue' : 'text-light-black/40'}`} />
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );
}
